import React, { useMemo } from 'react'

import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { facetTermDisplayText } from '@components/music/filter/utils/helpers'
import { AggregationTerm, Facet } from '@components/music/filter/utils/types'
import { urlFromFacet } from '@utils/formatters/url.formatter'
import PageRoutes from '@utils/page-routes'

interface Props {
  facet: Facet
}

const CategorySection: React.FC<Props> = ({ facet }) => {
  const { t } = useTranslation()
  const terms = useMemo(() => mapAndSortFacetTerms(facet), [facet])

  return (
    <section
      data-testid={`category-section-${facet.category}`}
      className="py-8 not-last:border-b border-gray-border dark:border-gray-border-dm"
      key={facet.category}>
      <h2 className="text-xl mb-2.5">{t(`content.browse.${facet.category}`)}</h2>
      <ul className="columns-2 sm:columns-3 md:columns-4">
        {terms.map((term) => (
          <li key={term.key} className="text-md mb-1.5">
            <Link
              to={PageRoutes.musicPath(urlFromFacet(term.category, term.key))}
              className="orange-link">
              {term.value}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  )
}

export default CategorySection

type SortedTerm = { category: string; key: string; value: string }
function mapAndSortFacetTerms(facet: Facet): SortedTerm[] {
  return facet.terms
    .map((term) => ({
      category: facet.category,
      key: term.key,
      value: facetTermDisplayText(facet.category, term.key, facet.translationPrefix),
    }))
    .sort((a, b) => {
      // Don't sort these alphabetically
      if (
        [
          AggregationTerm.DURATION,
          AggregationTerm.MUSIC_SOURCE,
          AggregationTerm.DIFFICULTY,
        ].includes(a.category as AggregationTerm)
      ) {
        return 0
      }
      return a.value.localeCompare(b.value)
    })
}
